/**
 * View / Edit slider in the header.
 *
 * Every session opens view-only. Sliding to Edit asks for the 4-digit edit PIN
 * (checked by the server); sliding back to View needs nothing. A session opened
 * from the cover page's past-sessions list is locked to View — the slider is
 * shown disabled so it is clear why nothing can be changed.
 *
 * The current mode is spelled out (VIEW/EDIT) inside the track itself, in the
 * space the knob has just vacated, rather than as a separate label beside the
 * switch — that used to need its own breakpoint to hide on narrow phones
 * (header-actions ran out of room), which dropped the mode entirely at that
 * width. Folding the label into the track keeps it legible everywhere.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/api/client';
import { useSession } from '@/contexts/SessionContext';

const PIN_LENGTH = 4;
const TRACK_W = 46;
const TRACK_H = 18;
const KNOB = 14;

export function EditModeToggle() {
  const { canEdit, accessMode, sessionId, setCanEdit } = useSession();
  const [showPin, setShowPin] = useState(false);
  const locked = accessMode === 'browse';

  const handleToggle = () => {
    if (locked) return;
    if (canEdit) {
      setCanEdit(false);
    } else {
      setShowPin(true);
    }
  };

  const title = locked
    ? 'Opened from past sessions — view only. Join with the session ID to edit.'
    : canEdit
      ? 'Editing — slide back to View to lock'
      : 'View only — slide to Edit (PIN required)';

  return (
    <>
      <button
        type="button"
        role="switch"
        aria-checked={canEdit}
        aria-label="Edit mode"
        title={title}
        onClick={handleToggle}
        disabled={locked}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: locked ? 'not-allowed' : 'pointer',
          color: 'white',
          opacity: locked ? 0.6 : 1,
          flexShrink: 0,
        }}
      >
        {locked && <span style={{ fontSize: 11, lineHeight: 1, flexShrink: 0 }}>🔒</span>}
        <span
          style={{
            position: 'relative',
            width: TRACK_W,
            height: TRACK_H,
            borderRadius: TRACK_H / 2,
            background: canEdit ? '#22c55e' : 'rgba(255,255,255,0.3)',
            border: '1px solid rgba(255,255,255,0.5)',
            transition: 'background 0.15s',
            flexShrink: 0,
          }}
        >
          {/* Annotation lives in the track, in the space the knob just left —
              not a separate label — so it's never the thing dropped to save
              width (see this file's header comment). */}
          <span
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: canEdit ? 4 : undefined,
              right: canEdit ? undefined : 4,
              display: 'flex',
              alignItems: 'center',
              fontSize: 7,
              fontWeight: 800,
              letterSpacing: '0.01em',
              lineHeight: 1,
              color: 'white',
            }}
          >
            {canEdit ? 'EDIT' : 'VIEW'}
          </span>
          <span
            style={{
              position: 'absolute',
              top: 1,
              left: canEdit ? TRACK_W - KNOB - 1 : 1,
              width: KNOB,
              height: KNOB,
              borderRadius: '50%',
              background: 'white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
              transition: 'left 0.15s',
            }}
          />
        </span>
      </button>

      {showPin && sessionId && (
        <PinDialog
          sessionId={sessionId}
          onCancel={() => setShowPin(false)}
          onVerified={(editToken) => { setShowPin(false); setCanEdit(true, editToken); }}
        />
      )}
    </>
  );
}

export function PinDialog({
  sessionId,
  onCancel,
  onVerified,
}: {
  sessionId: string;
  onCancel: () => void;
  onVerified: (editToken: string) => void;
}) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const submit = async (value: string) => {
    if (checking || value.length !== PIN_LENGTH) return;
    setChecking(true);
    setError('');
    try {
      const res = await apiClient.sessions.verifyPin(value, sessionId);
      if (res.valid && res.edit_token) {
        onVerified(res.edit_token);
        return;
      }
      setError('Wrong PIN.');
      setPin('');
      inputRef.current?.focus();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not check the PIN.');
    } finally {
      setChecking(false);
    }
  };

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <form
        onSubmit={(e) => { e.preventDefault(); void submit(pin); }}
        style={{
          background: 'white', borderRadius: 12, padding: 24, maxWidth: 320, width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)', color: '#1a1a2e',
        }}
      >
        <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700 }}>Enter edit PIN</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#555' }}>
          Enter the 4-digit PIN to switch this session to edit mode.
        </p>
        <input
          ref={inputRef}
          type="password"
          inputMode="numeric"
          autoComplete="off"
          pattern="[0-9]*"
          maxLength={PIN_LENGTH}
          value={pin}
          disabled={checking}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH);
            setPin(digits);
            setError('');
            if (digits.length === PIN_LENGTH) void submit(digits);
          }}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 14px', fontSize: 24,
            letterSpacing: 12, textAlign: 'center', fontFamily: 'monospace',
            border: `1px solid ${error ? '#dc3545' : '#d0d0d0'}`, borderRadius: 8, outline: 'none',
          }}
        />
        {error && <p style={{ color: '#dc3545', fontSize: 13, margin: '8px 0 0' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 16px', borderRadius: 6, border: '1px solid #d0d0d0', background: 'white',
              cursor: 'pointer', fontSize: 14, color: '#333',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={checking || pin.length !== PIN_LENGTH}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none', background: '#667eea', color: 'white',
              cursor: checking || pin.length !== PIN_LENGTH ? 'not-allowed' : 'pointer', fontSize: 14,
              fontWeight: 600, opacity: checking || pin.length !== PIN_LENGTH ? 0.6 : 1,
            }}
          >
            {checking ? 'Checking…' : 'Unlock'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
